import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, FileText, Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { api, ASSET_BASE, ASSETS } from "@/lib/api";
import { useLocale, setCanonical, clearHreflangs, restoreBaselineHead } from "@/lib/LocaleContext";
import { localisedTo } from "@/lib/i18nRoutes";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";

function fmtDate(iso, locale) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(locale === "ar" ? "ar-EG" : "en-US", { year: "numeric", month: "short", day: "numeric" });
  } catch {
    return "";
  }
}

export default function Blog() {
  const { t } = useTranslation();
  const { lang } = useLocale();
  const [items, setItems] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    document.title = lang === "ar"
      ? "مدونة DJ SPY — قصص الإصدارات"
      : "Blog · DJ SPY Official — Track breakdowns & release notes";
    // Meta description for the listing page
    let meta = document.querySelector('meta[name="description"]');
    if (!meta) {
      meta = document.createElement("meta");
      meta.setAttribute("name", "description");
      document.head.appendChild(meta);
    }
    meta.setAttribute(
      "content",
      lang === "ar"
        ? "تحليلات تفصيلية لكل ريمكس من DJ SPY — ملاحظات إنتاج وسياق ثقافي ونصائح للـ DJ."
        : "Deep dives into every DJ SPY remix — production notes, cultural context and playlist tips. Read the story behind each track.",
    );
    // Robots:
    //  - English listing: index,follow
    //  - Arabic listing: index,follow ONLY when items exist. If the AR
    //    catalog is empty we noindex to avoid an empty language variant.
    let robots = document.querySelector('meta[name="robots"]');
    if (!robots) {
      robots = document.createElement("meta");
      robots.setAttribute("name", "robots");
      document.head.appendChild(robots);
    }
    const hasItems = Array.isArray(items) && items.length > 0;
    const listingIndexable = lang !== "ar" || hasItems;
    robots.setAttribute("content", listingIndexable ? "index,follow" : "noindex,follow");
    // Self canonical; the listing advertises no language alternates.
    setCanonical(`https://djspyofficial.com${lang === "ar" ? "/ar/blog" : "/blog"}`);
    clearHreflangs();
  }, [lang, items]);

  useEffect(() => () => restoreBaselineHead(), []);

  useEffect(() => {
    let cancelled = false;
    api.get(`/blog?lang=${lang || "en"}&limit=1000`)
      .then((r) => { if (!cancelled) setItems(r.data.items || []); })
      .catch(() => { if (!cancelled) setError(t("blog.loadError")); });
    return () => { cancelled = true; };
  }, [t, lang]);

  return (
    <>
      <Nav />
      <main
        data-testid="page-blog"
        className="min-h-screen bg-black text-white pt-32 pb-24 px-6 md:px-10"
      >
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <p className="font-mono text-xs text-red-brand tracking-[0.3em]">{t("blog.kicker")}</p>
            <h1 className="font-anton text-5xl md:text-7xl mt-3 leading-none">{t("blog.title")}</h1>
            <p className="mt-4 text-white/70 max-w-2xl text-lg leading-relaxed">
              {t("blog.lead")}
            </p>
            {lang === "ar" && items && items.length === 0 && !error && (
              <div
                data-testid="ar-coming-soon-blog"
                className="mt-4 hairline rounded-xl border-yellow-500/30 bg-yellow-500/[0.06] px-4 py-2 text-[12px] font-mono text-yellow-200 tracking-wider inline-block"
              >
                {t("language.arabicComingSoon")}
              </div>
            )}
          </motion.div>

          <div className="mt-12">
            {error && (
              <div
                data-testid="blog-error"
                className="hairline rounded-xl p-6 bg-red-brand/5 border-red-brand/30 text-red-brand text-sm"
              >
                {error}
              </div>
            )}

            {!items && !error && (
              <div
                data-testid="blog-loading"
                className="flex items-center gap-3 text-white/50 font-mono text-xs tracking-widest"
              >
                <Loader2 size={14} className="animate-spin" /> {t("blog.loadingLabel")}
              </div>
            )}

            {items && items.length === 0 && !error && (
              <div
                data-testid="blog-empty"
                className="hairline rounded-2xl p-10 md:p-16 text-center bg-white/[0.02]"
              >
                <FileText size={36} className="mx-auto text-red-brand mb-4 opacity-70" />
                <h2 className="font-anton text-2xl md:text-3xl">{t("blog.emptyTitle")}</h2>
                <p className="text-white/60 mt-2 max-w-md mx-auto text-sm">
                  {t("blog.emptyBody")}
                </p>
                <Link to={localisedTo("/#music", lang)} className="btn-primary mt-6 inline-flex text-sm">
                  {t("blog.browseCatalogue")} <ArrowRight size={14} />
                </Link>
              </div>
            )}

            {items && items.length > 0 && (
              <div
                data-testid="blog-grid"
                className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8"
              >
                {items.map((p, i) => (
                  <motion.article
                    key={p.id}
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.35, delay: Math.min(i, 8) * 0.04 }}
                    data-testid={`blog-card-${p.slug}`}
                    className="group hairline rounded-2xl overflow-hidden bg-white/[0.02] hover:border-red-brand/40 transition-colors flex flex-col"
                  >
                    <Link to={localisedTo(`/blog/${p.slug}`, lang)} className="block">
                      <div className="aspect-[16/10] relative overflow-hidden bg-black/40">
                        <img
                          src={p.cover_url ? `${ASSET_BASE}${p.cover_url}` : ASSETS.logoCover}
                          alt={`${p.title} cover art`}
                          loading="lazy"
                          decoding="async"
                          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/10 to-transparent" />
                        {p.genre && (
                          <span className="absolute top-3 left-3 font-mono text-[10px] tracking-widest bg-black/70 backdrop-blur px-2 py-1 rounded text-white/80" dir="ltr">
                            {p.genre.toUpperCase()}
                          </span>
                        )}
                      </div>
                    </Link>

                    <div className="p-5 md:p-6 flex-1 flex flex-col">
                      <p className="font-mono text-[10px] tracking-widest text-white/40" dir="ltr">
                        {fmtDate(p.published_at, lang)}
                      </p>
                      <h2 className="font-display text-xl md:text-2xl mt-2 leading-tight text-white group-hover:text-red-brand transition-colors">
                        <Link to={localisedTo(`/blog/${p.slug}`, lang)}>{p.title}</Link>
                      </h2>
                      {p.artist && (
                        <p className="font-mono text-[10px] tracking-widest text-white/50 mt-1" dir="ltr">
                          ARTIST · {p.artist.toUpperCase()}
                        </p>
                      )}
                      {p.meta_description && (
                        <p className="text-sm text-white/65 mt-3 leading-relaxed line-clamp-3">
                          {p.meta_description}
                        </p>
                      )}
                      <Link
                        to={localisedTo(`/blog/${p.slug}`, lang)}
                        className="mt-5 inline-flex items-center gap-2 text-xs font-mono tracking-widest text-red-brand hover:gap-3 transition-all"
                        data-testid={`blog-read-${p.slug}`}
                      >
                        {t("blog.readStory")} <ArrowRight size={12} />
                      </Link>
                    </div>
                  </motion.article>
                ))}
              </div>
            )}
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}
